/**
 * BCM — Business Continuity + MyCiber Export.
 * Route: /bcm
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { ShieldAlert, Plus, Sparkles, Timer, Activity, Loader2, Trash2, PlayCircle, CheckCircle2, XCircle, FileDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VerifyReviewsButton } from '../components/VerifyReviewsButton';
import { DocumentsLink } from '../components/DocumentsLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  tier1_critical: { label: 'Tier 1 · Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  tier2_high: { label: 'Tier 2 · Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  tier3_medium: { label: 'Tier 3 · Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  tier4_low: { label: 'Tier 4 · Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function Bcm() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'ciso';
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testDialog, setTestDialog] = useState(null);
  const [testForm, setTestForm] = useState({ test_type: 'tabletop', date: new Date().toISOString().slice(0, 10), participants: '', outcome: 'success', actual_rto_hours: '', actual_rpo_hours: '', findings: '', corrective_actions: '' });
  const [downloading, setDownloading] = useState(false);

  const emptyForm = { name: '', description: '', tier: 'tier2_high', rto_hours: 12, rpo_hours: 6, strategy: '', owner_email: '', dependencies: '', next_test_date: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { document.title = 'Continuidade · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const [s, l] = await Promise.all([api.get('/bcm/stats'), api.get('/bcm/services')]);
        if (ignore) return;
        setStats(s.data);
        setItems(l.data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [refresh]);

  const seed = async () => {
    try {
      const { data } = await api.post('/bcm/services/seed');
      toast.success(`${data.inserted} serviços demo criados.`);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      name: svc.name, description: svc.description || '', tier: svc.tier,
      rto_hours: svc.rto_hours, rpo_hours: svc.rpo_hours,
      strategy: svc.strategy || '', owner_email: svc.owner_email || '',
      dependencies: (svc.dependencies || []).join(', '),
      next_test_date: svc.next_test_date || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name) { toast.error('Nome obrigatório.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, dependencies: form.dependencies.split(',').map(s => s.trim()).filter(Boolean), rto_hours: Number(form.rto_hours), rpo_hours: Number(form.rpo_hours) };
      if (editing) await api.patch(`/bcm/services/${editing.service_id}`, payload);
      else await api.post('/bcm/services', payload);
      toast.success(editing ? 'Atualizado.' : 'Criado.');
      setShowForm(false);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setSaving(false); }
  };

  const remove = async (svc) => {
    if (!confirm(`Remover ${svc.name}?`)) return;
    try {
      await api.delete(`/bcm/services/${svc.service_id}`);
      toast.success('Removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const submitTest = async () => {
    try {
      const payload = {
        ...testForm,
        participants: testForm.participants.split(',').map(s => s.trim()).filter(Boolean),
        actual_rto_hours: testForm.actual_rto_hours ? Number(testForm.actual_rto_hours) : null,
        actual_rpo_hours: testForm.actual_rpo_hours ? Number(testForm.actual_rpo_hours) : null,
      };
      await api.post(`/bcm/services/${testDialog.service_id}/tests`, payload);
      toast.success('Teste registado.');
      setTestDialog(null);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const downloadMyCiber = async (fmt) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${BACKEND_URL}/api/bcm/myciber-export.${fmt}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      if (fmt === 'pdf') {
        const blob = await res.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'myciber_export.pdf';
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'myciber_export.json';
        link.click();
        URL.revokeObjectURL(link.href);
      }
      toast.success(`Payload MyCiber exportado (${fmt.toUpperCase()}).`);
    } catch (err) { toast.error('Falha ao exportar.'); }
    finally { setDownloading(false); }
  };

  return (
    <Layout title="Continuidade de Negócio (BCM)">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="bcm-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-indigo-600" /> Continuidade de Negócio (BCM)
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Plano de continuidade por serviço crítico (<strong>NIS2 Art. 21(2)(c)</strong>) — RTO/RPO, estratégias de recuperação, testes tabletop e exportação MyCiber (CNCS).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => downloadMyCiber('pdf')} disabled={downloading} data-testid="myciber-pdf-btn">
                  {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />} MyCiber PDF
                </Button>
                <Button variant="outline" onClick={() => downloadMyCiber('json')} disabled={downloading} data-testid="myciber-json-btn">
                  <FileDown className="h-3.5 w-3.5 mr-1.5" /> MyCiber JSON
                </Button>
              </>
            )}
            {canEdit && <VerifyReviewsButton />}
            {canEdit && stats?.total_services === 0 && (
              <Button variant="outline" onClick={seed} data-testid="bcm-seed-btn"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Popular demo</Button>
            )}
            {canEdit && <Button onClick={openCreate} data-testid="bcm-new-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Novo serviço</Button>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'total_services', label: 'Serviços', value: stats?.total_services ?? 0, icon: Activity, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'tier1_critical', label: 'Tier 1 Crítico', value: stats?.tier1_critical ?? 0, icon: ShieldAlert, tone: 'from-red-500 to-red-600' },
            { key: 'untested_last_year', label: 'Sem teste 12m', value: stats?.untested_last_year ?? 0, icon: Timer, tone: 'from-amber-500 to-amber-600' },
            { key: 'tests_last_year', label: 'Testes 12m', value: stats?.tests_last_year ?? 0, icon: CheckCircle2, tone: 'from-emerald-500 to-emerald-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`bcm-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              items.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem serviços críticos. Use &quot;Popular demo&quot; ou &quot;Novo serviço&quot;.</div> :
              <div className="divide-y divide-slate-100">
                {items.map(s => {
                  const tm = TIER_META[s.tier] || TIER_META.tier2_high;
                  const testsCount = (s.tests || []).length;
                  const lastTest = testsCount > 0 ? s.tests[testsCount - 1] : null;
                  return (
                    <div key={s.service_id} className="p-4 hover:bg-slate-50" data-testid={`bcm-service-${s.service_id}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0"><Activity className="h-5 w-5 text-indigo-600" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-slate-900">{s.name}</h3>
                            <Badge variant="secondary" className={tm.tone}>{tm.label}</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">RTO {s.rto_hours}h · RPO {s.rpo_hours}h</Badge>
                            <span className="text-xs text-slate-500">· {testsCount} teste{testsCount !== 1 ? 's' : ''}</span>
                            {lastTest && (
                              <span className={`text-xs font-medium ${lastTest.outcome === 'success' ? 'text-emerald-600' : lastTest.outcome === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                                último: {lastTest.outcome} ({lastTest.date})
                              </span>
                            )}
                          </div>
                          {s.description && <p className="text-sm text-slate-600 mb-1">{s.description}</p>}
                          {s.strategy && <p className="text-xs text-slate-500"><strong>Estratégia:</strong> {s.strategy}</p>}
                          {(s.dependencies || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {s.dependencies.map((d, i) => <Badge key={i} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">{d}</Badge>)}
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <DocumentsLink module="bcm" entityId={s.service_id} title={s.name} />
                            <Button variant="ghost" size="sm" onClick={() => setTestDialog(s)} data-testid={`bcm-test-btn-${s.service_id}`} title="Registar teste"><PlayCircle className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`bcm-edit-btn-${s.service_id}`}>Editar</Button>
                            {user?.role === 'admin' && <Button variant="ghost" size="sm" onClick={() => remove(s)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            }
          </CardContent>
        </Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar: ${editing.name}` : 'Novo serviço crítico'}</DialogTitle>
            <DialogDescription>Defina RTO/RPO e estratégia de recuperação. Obrigatório para NIS2 Art. 21(2)(c).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="bcm-form-name" /></div>
            <div><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Tier</Label>
                <Select value={form.tier} onValueChange={v => setForm({ ...form, tier: v })}>
                  <SelectTrigger data-testid="bcm-form-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TIER_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>RTO (horas) *</Label><Input type="number" min={0} max={720} value={form.rto_hours} onChange={e => setForm({ ...form, rto_hours: e.target.value })} data-testid="bcm-form-rto" /></div>
              <div><Label>RPO (horas) *</Label><Input type="number" min={0} max={720} value={form.rpo_hours} onChange={e => setForm({ ...form, rpo_hours: e.target.value })} data-testid="bcm-form-rpo" /></div>
            </div>
            <div><Label>Estratégia de recuperação</Label><Textarea rows={2} value={form.strategy} onChange={e => setForm({ ...form, strategy: e.target.value })} placeholder="Hot standby AWS multi-AZ, backup snapshot horário…" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Owner (email)</Label><Input value={form.owner_email} onChange={e => setForm({ ...form, owner_email: e.target.value })} /></div>
              <div><Label>Dependências (vírgula)</Label><Input value={form.dependencies} onChange={e => setForm({ ...form, dependencies: e.target.value })} placeholder="AWS, DNS, MPLS" /></div>
            </div>
            <div>
              <Label>Próximo teste BCM</Label>
              <Input
                type="date"
                value={form.next_test_date || ''}
                onChange={e => setForm({ ...form, next_test_date: e.target.value })}
                data-testid="bcm-form-next-test"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Define a data prevista para o próximo teste (tabletop/full-scale). O sistema criará automaticamente tarefa e alerta 90/30/7/1 dias antes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="bcm-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : editing ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!testDialog} onOpenChange={(o) => { if (!o) setTestDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registar teste · {testDialog?.name}</DialogTitle>
            <DialogDescription>Evidência obrigatória para auditoria — RTO/RPO reais medidos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={testForm.test_type} onValueChange={v => setTestForm({ ...testForm, test_type: v })}>
                  <SelectTrigger data-testid="test-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tabletop">Tabletop</SelectItem>
                    <SelectItem value="walkthrough">Walkthrough</SelectItem>
                    <SelectItem value="simulation">Simulação</SelectItem>
                    <SelectItem value="full_scale">Full-scale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Data *</Label><Input type="date" value={testForm.date} onChange={e => setTestForm({ ...testForm, date: e.target.value })} data-testid="test-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Outcome</Label>
                <Select value={testForm.outcome} onValueChange={v => setTestForm({ ...testForm, outcome: v })}>
                  <SelectTrigger data-testid="test-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="success">Sucesso</SelectItem>
                    <SelectItem value="partial">Parcial</SelectItem>
                    <SelectItem value="failed">Falhou</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Participantes (vírgula)</Label><Input value={testForm.participants} onChange={e => setTestForm({ ...testForm, participants: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>RTO real medido (h)</Label><Input type="number" step="0.1" value={testForm.actual_rto_hours} onChange={e => setTestForm({ ...testForm, actual_rto_hours: e.target.value })} /></div>
              <div><Label>RPO real medido (h)</Label><Input type="number" step="0.1" value={testForm.actual_rpo_hours} onChange={e => setTestForm({ ...testForm, actual_rpo_hours: e.target.value })} /></div>
            </div>
            <div><Label>Findings</Label><Textarea rows={2} value={testForm.findings} onChange={e => setTestForm({ ...testForm, findings: e.target.value })} /></div>
            <div><Label>Ações corretivas</Label><Textarea rows={2} value={testForm.corrective_actions} onChange={e => setTestForm({ ...testForm, corrective_actions: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestDialog(null)}>Cancelar</Button>
            <Button onClick={submitTest} data-testid="test-submit">Registar teste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
